import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { Pic } from "./Pic";
import { AUDIENCES } from "../data/content";

const BLURBS = {
  corporate: "Leadership development and large-scale corporate events & conferences.",
  "country-clubs": "Member events that feel like the highlight of the season.",
  communities: "Live, hosted games that bring neighbors together.",
  charities: "Fundraisers and galas with real participation.",
  private: "Celebrations built around your people.",
};

const STARS = [
  // mobile (<768px): corners only, clear of eyebrow/headline/subtext/link/cards
  { c: "right-4 top-4 w-9 -rotate-12 md:hidden", d: "-1.2s", t: "3.6s" }, { c: "right-16 top-9 w-4 md:hidden", d: "-2.1s", t: "2.6s", white: true },
  { c: "left-4 bottom-4 w-8 -rotate-6 md:hidden", d: "-2.8s", t: "2.9s" }, { c: "right-6 bottom-5 w-5 rotate-45 md:hidden", d: "-0.7s", t: "3.9s" },
  // tablet / desktop
  { c: "left-[2%] top-3 w-10 rotate-12 hidden md:block", d: "0s", t: "3.1s" }, { c: "right-[6%] top-[8%] w-12 -rotate-12 hidden md:block", d: "-1.2s", t: "3.6s" }, { c: "left-[38%] top-[4%] w-5 hidden md:block", d: "-2.1s", t: "2.6s" },
  { c: "right-[30%] bottom-3 w-7 rotate-45 hidden md:block", d: "-0.7s", t: "3.9s" }, { c: "left-[12%] bottom-3 w-9 -rotate-6 hidden lg:block", d: "-2.8s", t: "2.9s" }, { c: "right-[40%] top-[5%] w-4 hidden md:block", d: "-1.6s", t: "3.3s" },
  { c: "left-[22%] top-5 w-4 rotate-6 hidden md:block", d: "-3.2s", t: "2.7s", white: true }, { c: "right-[20%] top-[14%] w-5 hidden md:block", d: "-0.4s", t: "3.8s", white: true },
];

const StarShape = ({ className, white, style, ...rest }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden {...rest}>
    <path d="M12 1.5 C12.8 7.5 16.5 11.2 22.5 12 C16.5 12.8 12.8 16.5 12 22.5 C11.2 16.5 7.5 12.8 1.5 12 C7.5 11.2 11.2 7.5 12 1.5 Z" fill={white ? "#ffffff" : "#FFC20E"} />
  </svg>
);

export const AudienceCards = () => (
  <section data-testid="home-who-we-serve" className="relative isolate overflow-hidden py-20 text-white lg:py-28" style={{ background: "radial-gradient(120% 90% at 85% 0%, #1fb3b6 0%, #149b9e 45%, #0f7f82 100%)" }}>
    {STARS.map((s, i) => (
      <StarShape key={i} white={s.white} className={`glow-star pointer-events-none absolute -z-10 ${s.c}`} style={{ "--d": s.d, "--t": s.t }} data-testid={`wws-star-${i}`} />
    ))}
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl lg:max-w-none">
          <p className="font-gothic uppercase tracking-[0.2em] text-xs sm:text-sm text-white">Who we work with</p>
          <h2 className="font-display mt-4 text-3xl leading-[1.05] text-white sm:text-4xl lg:text-5xl">Fun that fits every crowd.</h2>
          <p className="mt-4 text-base text-white sm:text-lg lg:whitespace-nowrap">Live-hosted games for corporate teams, clubs, communities, charities and private events.</p>
        </div>
        <Link to="/who-we-serve" data-testid="home-who-we-serve-link" className="font-gothic inline-flex items-center gap-2 uppercase tracking-wider text-sm text-white underline-offset-4 hover:underline">
          Learn more about who we serve <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>

      <div data-testid="audience-card-grid" className="-mx-5 mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 scroll-pl-5 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 md:grid-cols-3 lg:grid-cols-5">
        {AUDIENCES.map((a, i) => (
          <Reveal key={a.slug} delay={0.08 * i} className="w-[78%] shrink-0 snap-start sm:w-auto">
            <Link to={`/who-we-serve#${a.slug}`} data-testid={`home-audience-card-${a.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl bg-white text-black shadow-[0_20px_50px_-25px_rgba(0,0,0,0.5)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.6)]">
              <div className="aspect-[4/3] overflow-hidden">
                <Pic src={a.homeImage || a.image} alt={a.homeAlt || a.alt} sizes="(min-width: 1024px) 20vw, (min-width: 640px) 50vw, 78vw" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-xl leading-tight">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-black/65">{BLURBS[a.slug]}</p>
                <span className="font-gothic mt-4 inline-flex items-center gap-1 uppercase tracking-wider text-xs text-en-teal transition-[gap] duration-200 group-hover:gap-2">See how <ArrowRight className="h-3.5 w-3.5" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
